/**
 * 听写播放器的**纯逻辑**：把一个会话的题目 + 节奏编排成一条可执行的播放时间线。
 *
 * ## 为什么要把"播放顺序与节奏"抽成纯函数
 *
 * 听写能不能用，全靠这几个数：读几遍、之间停几秒、词与词之间停几秒。而这些
 * 正好是最容易写错、又最难在浏览器里手测的东西（要真的听完一整轮才知道错了）。
 * 所以这里不碰 DOM、不碰计时器，只回答三个问题：
 *
 *   1. **按这个节奏播放，时间线上依次会发生什么？**（`buildPlaybackPlan`）
 *   2. **总共要多久？**（`totalPlannedMs` / `estimateDictationSeconds`）
 *   3. **"跳到第 5 题"意味着时间线上的哪一格？**（`wordStartIndex`，用于重播/跳过）
 *
 * 界面上的计时器只是"按这条时间线走"的执行器；它自己不做节奏运算。
 *
 * ## 时间线的形状（与 `tempo.ts` 的口径完全一致）
 *
 * 每个词展开成：
 *
 *     [读第 1 遍] ·停gap· [读第 2 遍] ·停gap· … ·停gap· ··再停between··
 *      └────────── repeatCount 次 ──────────┘  └ 每个词都有 ┘ └ 只有词间有 ┘
 *
 * 也就是说：
 *   · `gapSeconds` 出现 `repeatCount` 次（同词两遍之间各一次，**最后一次读完之后
 *     也有一次** —— 那是"写完这个词"的书写时间）；
 *   · `betweenWordsSeconds` 出现 `n-1` 次（**只**在词与词之间）。
 *
 * `between` 与 `gap` 在时间线上是**两格**（不是一个"加起来的值"）：这样
 * 界面上可以分别显示"还有 5 秒再读一遍"和"还有 12 秒换下一个词"，
 * 而不是让孩子盯着一个来历不明的 17 秒。也让单元测试能逐格断言。
 *
 * ## ⚠️ 自动播放限制（浏览器策略，不是本模块能解决的）
 *
 * `speechSynthesis.speak()` 与 `<audio>.play()` 都受"用户手势"限制：
 * **页面加载时自动出声一定会被浏览器拦掉**。所以时间线的第一格永远是"等一个
 * 用户手势"（`initialPlaybackState` 的 `startedSinceGesture: false`），
 * 界面必须先渲染一个「开始播放」按钮。这一条是硬约束，不是可选的体验优化。
 */
import type { DictationTempoConfig } from './tempo.js';

/** 时间线上的一个位置。 */
export interface PlaybackStep {
  /** 在整个时间线上的序号（从 0 开始），`wordStartIndex` 返回的就是它。 */
  index: number;
  kind: 'word' | 'gap' | 'between';
  /** 第几题（从 1 开始）。`gap` / `between` 归属于它**前面**那一题。 */
  wordPosition: number;
  /** `word`：这是这个词的第几遍（从 1 开始）；其它 kind 恒为 1。 */
  repeat: number;
  /** 这一格持续多少毫秒：`word` 是朗读时长，`gap`/`between` 是等待时长。 */
  durationMs: number;
}

/** 一个词的播放来源（决定这一格交给谁去出声）。 */
export type SpokenSource = 'parent_audio' | 'tts';

/** 编排时间线需要的、关于一个词的最小信息。 */
export interface PlaybackWord {
  /** 题号（从 1 开始）。 */
  position: number;
  /** 朗读来源（家长录音 / 浏览器语音）。 */
  source: SpokenSource;
  /** 家长录音的真实时长（毫秒）；没有录音或浏览器没给时长时是 `null`。 */
  durationMs: number | null;
  /** 没有录音时的朗读文本（**永远是词本身**：`speechSynthesis` 读不了拼音）。 */
  speakText: string;
}

export interface PlaybackPlan {
  steps: PlaybackStep[];
  /** 时间线上每一题的起点（键 = 题号，值 = `steps` 的下标）。 */
  wordStartIndex: ReadonlyMap<number, number>;
  totalMs: number;
  /** 预计读数时长合计（`word` 格之和）。 */
  spokenMs: number;
  /** 预计等待时长合计（`gap` + `between` 格之和）。 */
  waitingMs: number;
}

/** 没有真实时长时，按字数估一个朗读时长：约 0.45 秒/字，最少 1.2 秒。 */
export function estimateSpokenMs(text: string): number {
  const chars = [...text].length;
  return Math.max(1200, Math.round(chars * 450));
}

/** 某一格的时长：有真实录音时长就用它，否则按文本估。 */
function spokenMsOf(word: PlaybackWord): number {
  if (typeof word.durationMs === 'number' && Number.isFinite(word.durationMs) && word.durationMs > 0) {
    return Math.round(word.durationMs);
  }
  return estimateSpokenMs(word.speakText);
}

/**
 * 编排时间线。
 *
 * @param words 按题号顺序排列的词
 * @param tempo 节奏设置（**已补齐默认值**，缺项会算出 NaN 遍 —— 见 `withTempoDefaults`）
 */
export function buildPlaybackPlan(
  words: readonly PlaybackWord[],
  tempo: DictationTempoConfig,
): PlaybackPlan {
  const steps: PlaybackStep[] = [];
  const wordStartIndex = new Map<number, number>();

  /**
   * 节奏里的数一律夹到 ≥0 且取整：`jsonb` 里存的是数字，但一个手工改过的
   * 负数会变成"负的等待"，让时间线往回走。宁可夹到 0（等价于"不停"）。
   */
  const repeatCount = Math.max(1, Math.floor(tempo.repeatCount) || 1);
  const gapMs = Math.max(0, Math.round(tempo.gapSeconds * 1000));
  const betweenMs = Math.max(0, Math.round(tempo.betweenWordsSeconds * 1000));

  words.forEach((word, wordIndex) => {
    wordStartIndex.set(word.position, steps.length);
    for (let repeat = 1; repeat <= repeatCount; repeat += 1) {
      steps.push({
        index: steps.length,
        kind: 'word',
        wordPosition: word.position,
        repeat,
        durationMs: spokenMsOf(word),
      });
      // 每一遍读完之后都停 gap：最后一篇之后的那次就是"写完这个词"的时间。
      if (gapMs > 0) {
        steps.push({
          index: steps.length,
          kind: 'gap',
          wordPosition: word.position,
          repeat,
          durationMs: gapMs,
        });
      }
    }
    // 词与词之间额外再停一次（最后一个词后面没有"下一个词"，所以不停）。
    if (betweenMs > 0 && wordIndex < words.length - 1) {
      steps.push({
        index: steps.length,
        kind: 'between',
        wordPosition: word.position,
        repeat: repeatCount,
        durationMs: betweenMs,
      });
    }
  });

  let spokenMs = 0;
  let waitingMs = 0;
  for (const step of steps) {
    if (step.kind === 'word') spokenMs += step.durationMs;
    else waitingMs += step.durationMs;
  }

  return { steps, wordStartIndex, totalMs: spokenMs + waitingMs, spokenMs, waitingMs };
}

// ---------------------------------------------------------------- 播放状态

/**
 * 播放器的状态。
 *
 * `stepIndex` = 已经**播完**了多少格（所以 `0` 表示一格都没播，下一格是 `steps[0]`）。
 * `finished` 与 `stepIndex >= steps.length` 是同一件事的两种说法，两个都留着是因为
 * 界面要区分"还没开始"（`stepIndex===0 && !finished`）与"已经走完"
 * —— 空时间线（0 个词）时两者都成立，界面必须显式处理，不能让它显示成"播放完毕"。
 */
export interface PlaybackState {
  stepIndex: number;
  playing: boolean;
  finished: boolean;
  /**
   * 是否已经发生过一次**用户手势**。
   *
   * ⚠️ 这是自动播放限制的落点：页面上任何"开始/继续播放"都必须要求它为 `true`，
   * 否则 `speak()` / `play()` 会被浏览器拒绝（iOS Safari 上表现为**完全没声音**
   * 且没有任何报错 —— 最难查的那种）。
   */
  startedSinceGesture: boolean;
}

export function initialPlaybackState(): PlaybackState {
  return { stepIndex: 0, playing: false, finished: false, startedSinceGesture: false };
}

/** 用户在「开始播放」上点了一下：标记手势、从头播。 */
export function startPlayback(state: PlaybackState): PlaybackState {
  return { ...state, playing: true, finished: false, startedSinceGesture: true };
}

/** 暂停（不改变位置）。 */
export function pausePlayback(state: PlaybackState): PlaybackState {
  return { ...state, playing: false };
}

/** 从当前格继续（仍然要求先有过手势）。 */
export function resumePlayback(state: PlaybackState): PlaybackState {
  return { ...state, playing: true };
}

/** 播完一格，前进。走完最后一格时置 `finished`。 */
export function advancePlayback(state: PlaybackState, stepCount: number): PlaybackState {
  const next = state.stepIndex + 1;
  const done = stepCount <= 0 || next >= stepCount;
  return { ...state, stepIndex: Math.min(next, Math.max(0, stepCount)), finished: done, playing: done ? false : state.playing };
}

/**
 * 跳到某一题（重播当前题 / 跳过 / 手点题号）。
 *
 * 跳转 == 把 `stepIndex` 移到那一题第一格的**起点**，也就是"这一题还没播"。
 * 一个不存在的题号原样返回（界面据此不做任何事，而不是把播放器弄到一个 0 长度的时间线上）。
 */
export function seekToWord(state: PlaybackState, plan: PlaybackPlan, position: number): PlaybackState {
  const index = plan.wordStartIndex.get(position);
  if (index === undefined) return state;
  return { ...state, stepIndex: index, finished: false };
}

/**
 * 当前正在播放/等待的那一格；没有（未开始 / 已播完）时返回 `null`。
 *
 * 界面上的"第 3 题 / 共 20 题"与"正在读第 2 遍"都从这里推，不自己另算一遍。
 */
export function currentStep(state: PlaybackState, plan: PlaybackPlan): PlaybackStep | null {
  if (state.finished) return null;
  return plan.steps[state.stepIndex] ?? null;
}

/**
 * 已经播到第几题（用于"第 3 题 / 共 20 题"）。
 *
 * 未开始时返回第一题的题号（`steps[0].wordPosition`）—— 界面要显示"准备读第 1 题"
 * 而不是「—」。空时间线返回 `null`（**不是 0**：0 不是合法的题号，用 0 会让界面
 * 显示"第 0 题"）。
 */
export function currentPosition(state: PlaybackState, plan: PlaybackPlan): number | null {
  const step = currentStep(state, plan) ?? plan.steps[plan.steps.length - 1];
  return step?.wordPosition ?? null;
}

/** 进度（0~1）。空时间线返回 0（**不是 NaN**）。 */
export function playbackProgress(state: PlaybackState, plan: PlaybackPlan): number {
  if (plan.totalMs <= 0) return 0;
  let done = 0;
  for (let i = 0; i < state.stepIndex && i < plan.steps.length; i += 1) {
    done += plan.steps[i]!.durationMs;
  }
  return Math.max(0, Math.min(1, done / plan.totalMs));
}

// ---------------------------------------------------------------- 朗读文本

/**
 * 这个时间线格**该念什么**。
 *
 * ⚠️ 三条不能动：
 *   1. 家长录音（`source === 'parent_audio'`）→ **不出声**（`text: null`），
 *      由界面播 `<audio>`。**绝不能**"顺手也用 TTS 念一遍"：那正是家长录音
 *      要覆盖掉的东西（读错的音）。
 *   2. TTS 的朗读文本永远是**词本身**，**不是拼音** —— `speechSynthesis`
 *      只吃文字。第一步的"人工覆盖拼音"解决的是显示与数据，解决不了朗读，
 *      所以录音覆盖是唯一可靠的路（见 `docs/接口契约对齐.md`）。
 *   3. `gap` / `between` 格是静音（`text: null`），但仍然占时间。
 */
export function speechTextFor(step: PlaybackStep, word: PlaybackWord | undefined): string | null {
  if (step.kind !== 'word') return null;
  if (word === undefined) return null;
  if (word.source === 'parent_audio') return null;
  return word.speakText;
}
